"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GumballTransformGizmo = void 0;
const viewer_shared_services_1 = require("@shapediver/viewer.shared.services");
const three_1 = require("three");
const GumballTransformControls_1 = require("./GumballTransformControls");
class GumballTransformGizmo extends three_1.Object3D {
    constructor(_gumballTransformControls) {
        super();
        this._gumballTransformControls = _gumballTransformControls;
        this._enableRotation = true;
        this._enableRotationX = true;
        this._enableRotationXY = true;
        this._enableRotationXZ = true;
        this._enableRotationY = true;
        this._enableRotationYZ = true;
        this._enableRotationZ = true;
        this._enableScaling = true;
        this._enableScalingX = true;
        this._enableScalingXY = true;
        this._enableScalingXZ = true;
        this._enableScalingY = true;
        this._enableScalingYZ = true;
        this._enableScalingZ = true;
        this._enableTranslation = true;
        this._enableTranslationX = true;
        this._enableTranslationXY = true;
        this._enableTranslationXZ = true;
        this._enableTranslationY = true;
        this._enableTranslationYZ = true;
        this._enableTranslationZ = true;
        this.isGumballTransformGizmo = true;
        this.type = "GumballTransformGizmo";
        const isMobile = viewer_shared_services_1.SystemInfo.instance.isMobile;
        const mobileFactorSingleAxis = isMobile ? 2 : 1;
        const mobileFactorMultiAxis = isMobile ? 1.5 : 1;
        // shared materials
        const gizmoMaterial = new three_1.MeshBasicMaterial({
            depthTest: false,
            depthWrite: false,
            fog: false,
            toneMapped: false,
            transparent: true,
        });
        const gizmoLineMaterial = new three_1.LineBasicMaterial({
            depthTest: false,
            depthWrite: false,
            fog: false,
            toneMapped: false,
            transparent: true,
        });
        // Make unique material for each axis/color
        const materialInvisible = gizmoMaterial.clone();
        materialInvisible.opacity = 0.15;
        const translationMaterialInvisible = materialInvisible.clone();
        const rotationMaterialInvisible = materialInvisible.clone();
        const scaleMaterialInvisible = materialInvisible.clone();
        const materialHelper = gizmoLineMaterial.clone();
        materialHelper.opacity = 0.5;
        const translationMaterialHelper = materialHelper.clone();
        const rotationMaterialHelper = materialHelper.clone();
        const scaleMaterialHelper = materialHelper.clone();
        const materialRed = gizmoMaterial.clone();
        materialRed.color.setHex(0xff0000);
        const translationMaterialRed = materialRed.clone();
        const rotationMaterialRed = materialRed.clone();
        const scaleMaterialRed = materialRed.clone();
        const materialGreen = gizmoMaterial.clone();
        materialGreen.color.setHex(0x00ff00);
        const translationMaterialGreen = materialGreen.clone();
        const rotationMaterialGreen = materialGreen.clone();
        const scaleMaterialGreen = materialGreen.clone();
        const materialBlue = gizmoMaterial.clone();
        materialBlue.color.setHex(0x0000ff);
        const translationMaterialBlue = materialBlue.clone();
        const rotationMaterialBlue = materialBlue.clone();
        const scaleMaterialBlue = materialBlue.clone();
        const materialRedTransparent = gizmoMaterial.clone();
        materialRedTransparent.color.setHex(0xff0000);
        materialRedTransparent.opacity = 0.5;
        const translationMaterialRedTransparent = materialRedTransparent.clone();
        const scaleMaterialRedTransparent = materialRedTransparent.clone();
        const materialGreenTransparent = gizmoMaterial.clone();
        materialGreenTransparent.color.setHex(0x00ff00);
        materialGreenTransparent.opacity = 0.5;
        const translationMaterialGreenTransparent = materialGreenTransparent.clone();
        const scaleMaterialGreenTransparent = materialGreenTransparent.clone();
        const materialBlueTransparent = gizmoMaterial.clone();
        materialBlueTransparent.color.setHex(0x0000ff);
        materialBlueTransparent.opacity = 0.5;
        const translationMaterialBlueTransparent = materialBlueTransparent.clone();
        const scaleMaterialBlueTransparent = materialBlueTransparent.clone();
        const materialWhiteTransparent = gizmoMaterial.clone();
        materialWhiteTransparent.opacity = 0.25;
        const translationMaterialWhiteTransparent = materialWhiteTransparent.clone();
        const scaleMaterialWhiteTransparent = materialWhiteTransparent.clone();
        const materialYellowTransparent = gizmoMaterial.clone();
        materialYellowTransparent.color.setHex(0xffff00);
        materialYellowTransparent.opacity = 0.25;
        const rotationMaterialYellowTransparent = materialYellowTransparent.clone();
        const materialGray = gizmoMaterial.clone();
        materialGray.color.setHex(0x787878);
        const rotationMaterialGray = materialGray.clone();
        // reusable geometry
        const arrowGeo = new three_1.CylinderGeometry(0, 0.04, 0.1, 12);
        arrowGeo.translate(0, 0.05, 0);
        const translationArrowGeometry = arrowGeo.clone();
        const scaleHandleGeometry = new three_1.BoxGeometry(0.08, 0.08, 0.08);
        scaleHandleGeometry.translate(0, 0.04, 0);
        const lineGeo = new three_1.BufferGeometry();
        lineGeo.setAttribute("position", new three_1.Float32BufferAttribute([0, 0, 0, 1, 0, 0], 3));
        const translationLineGeometry = lineGeo.clone();
        const scaleLineGeometry = lineGeo.clone();
        const rotationLineGeometry = lineGeo.clone();
        const lineGeo2 = new three_1.CylinderGeometry(0.0075, 0.0075, 0.5, 3);
        lineGeo2.translate(0, 0.25, 0);
        const translationLineGeometry2 = lineGeo2.clone();
        function CircleGeometry(radius, arc) {
            const geometry = new three_1.TorusGeometry(radius, 0.0075, 3, 64, arc * Math.PI * 2);
            geometry.rotateY(Math.PI / 2);
            geometry.rotateX(Math.PI / 2);
            return geometry;
        }
        // Special geometry for transform helper. If scaled with position vector it spans from [0,0,0] to position
        function TranslateHelperGeometry() {
            const geometry = new three_1.BufferGeometry();
            geometry.setAttribute("position", new three_1.Float32BufferAttribute([0, 0, 0, 1, 1, 1], 3));
            return geometry;
        }
        const gizmoTranslate = {
            X: [
                [
                    new three_1.Mesh(translationArrowGeometry, translationMaterialRed),
                    [0.5, 0, 0],
                    [0, 0, -Math.PI / 2],
                ],
                // [new Mesh(translationArrowGeometry, translationMaterialRed), [- 0.5, 0, 0], [0, 0, Math.PI / 2]],
                [
                    new three_1.Mesh(translationLineGeometry2, translationMaterialRed),
                    [0, 0, 0],
                    [0, 0, -Math.PI / 2],
                ],
            ],
            Y: [
                [
                    new three_1.Mesh(translationArrowGeometry, translationMaterialGreen),
                    [0, 0.5, 0],
                ],
                // [new Mesh(translationArrowGeometry, translationMaterialGreen), [0, - 0.5, 0], [Math.PI, 0, 0]],
                [new three_1.Mesh(translationLineGeometry2, translationMaterialGreen)],
            ],
            Z: [
                [
                    new three_1.Mesh(translationArrowGeometry, translationMaterialBlue),
                    [0, 0, 0.5],
                    [Math.PI / 2, 0, 0],
                ],
                // [new Mesh(translationArrowGeometry, translationMaterialBlue), [0, 0, - 0.5], [- Math.PI / 2, 0, 0]],
                [
                    new three_1.Mesh(translationLineGeometry2, translationMaterialBlue),
                    null,
                    [Math.PI / 2, 0, 0],
                ],
            ],
            XYZ: [
                [
                    new three_1.Mesh(new three_1.OctahedronGeometry(0.1, 0), translationMaterialWhiteTransparent.clone()),
                    [0, 0, 0],
                ],
            ],
            XY: [
                [
                    new three_1.Mesh(new three_1.BoxGeometry(0.15, 0.15, 0.01), translationMaterialBlueTransparent.clone()),
                    [
                        0.25 * mobileFactorMultiAxis,
                        0.25 * mobileFactorMultiAxis,
                        0,
                    ],
                ],
            ],
            YZ: [
                [
                    new three_1.Mesh(new three_1.BoxGeometry(0.15, 0.15, 0.01), translationMaterialRedTransparent.clone()),
                    [
                        0,
                        0.25 * mobileFactorMultiAxis,
                        0.25 * mobileFactorMultiAxis,
                    ],
                    [0, Math.PI / 2, 0],
                ],
            ],
            XZ: [
                [
                    new three_1.Mesh(new three_1.BoxGeometry(0.15, 0.15, 0.01), translationMaterialGreenTransparent.clone()),
                    [
                        0.25 * mobileFactorMultiAxis,
                        0,
                        0.25 * mobileFactorMultiAxis,
                    ],
                    [-Math.PI / 2, 0, 0],
                ],
            ],
        };
        const pickerTranslate = {
            X: [
                [
                    new three_1.Mesh(new three_1.CylinderGeometry(0.15 * mobileFactorSingleAxis, 0.15 * mobileFactorSingleAxis, 0.6, 4), translationMaterialInvisible),
                    [0.3, 0, 0],
                    [0, 0, -Math.PI / 2],
                ],
                // [new Mesh(new CylinderGeometry(0.2, 0, 0.6, 4), translationMaterialInvisible), [- 0.3, 0, 0], [0, 0, Math.PI / 2]]
            ],
            Y: [
                [
                    new three_1.Mesh(new three_1.CylinderGeometry(0.15 * mobileFactorSingleAxis, 0.15 * mobileFactorSingleAxis, 0.6, 4), translationMaterialInvisible),
                    [0, 0.3, 0],
                ],
                // [new Mesh(new CylinderGeometry(0.2, 0, 0.6, 4), translationMaterialInvisible), [0, - 0.3, 0], [0, 0, Math.PI]]
            ],
            Z: [
                [
                    new three_1.Mesh(new three_1.CylinderGeometry(0.15 * mobileFactorSingleAxis, 0.15 * mobileFactorSingleAxis, 0.6, 4), translationMaterialInvisible),
                    [0, 0, 0.3],
                    [Math.PI / 2, 0, 0],
                ],
                // [new Mesh(new CylinderGeometry(0.2, 0, 0.6, 4), translationMaterialInvisible), [0, 0, - 0.3], [- Math.PI / 2, 0, 0]]
            ],
            XYZ: [
                [
                    new three_1.Mesh(new three_1.OctahedronGeometry(0.2, 0), translationMaterialInvisible),
                ],
            ],
            XY: [
                [
                    new three_1.Mesh(new three_1.BoxGeometry(0.2 * mobileFactorMultiAxis, 0.2 * mobileFactorMultiAxis, 0.01 * mobileFactorMultiAxis), translationMaterialInvisible),
                    [
                        0.25 * mobileFactorMultiAxis,
                        0.25 * mobileFactorMultiAxis,
                        0,
                    ],
                ],
            ],
            YZ: [
                [
                    new three_1.Mesh(new three_1.BoxGeometry(0.2 * mobileFactorMultiAxis, 0.2 * mobileFactorMultiAxis, 0.01 * mobileFactorMultiAxis), translationMaterialInvisible),
                    [
                        0,
                        0.25 * mobileFactorMultiAxis,
                        0.25 * mobileFactorMultiAxis,
                    ],
                    [0, Math.PI / 2, 0],
                ],
            ],
            XZ: [
                [
                    new three_1.Mesh(new three_1.BoxGeometry(0.2 * mobileFactorMultiAxis, 0.2 * mobileFactorMultiAxis, 0.01 * mobileFactorMultiAxis), translationMaterialInvisible),
                    [
                        0.25 * mobileFactorMultiAxis,
                        0,
                        0.25 * mobileFactorMultiAxis,
                    ],
                    [-Math.PI / 2, 0, 0],
                ],
            ],
        };
        const helperTranslate = {
            START: [
                [
                    new three_1.Mesh(new three_1.OctahedronGeometry(0.01, 2), translationMaterialHelper),
                    null,
                    null,
                    null,
                    "helper",
                ],
            ],
            END: [
                [
                    new three_1.Mesh(new three_1.OctahedronGeometry(0.01, 2), translationMaterialHelper),
                    null,
                    null,
                    null,
                    "helper",
                ],
            ],
            DELTA: [
                [
                    new three_1.Line(TranslateHelperGeometry(), translationMaterialHelper),
                    null,
                    null,
                    null,
                    "helper",
                ],
            ],
            X: [
                [
                    new three_1.Line(translationLineGeometry, translationMaterialHelper.clone()),
                    [-1e3, 0, 0],
                    null,
                    [1e6, 1, 1],
                    "helper",
                ],
            ],
            Y: [
                [
                    new three_1.Line(translationLineGeometry, translationMaterialHelper.clone()),
                    [0, -1e3, 0],
                    [0, 0, Math.PI / 2],
                    [1e6, 1, 1],
                    "helper",
                ],
            ],
            Z: [
                [
                    new three_1.Line(translationLineGeometry, translationMaterialHelper.clone()),
                    [0, 0, -1e3],
                    [0, -Math.PI / 2, 0],
                    [1e6, 1, 1],
                    "helper",
                ],
            ],
        };
        const rotationScale = 1.5;
        const gizmoRotate = {
            // XYZE: [
            //     [new Mesh(CircleGeometry(0.5 * rotationScale, 1), rotationMaterialGray), null, [0, Math.PI / 2, 0]]
            // ],
            X: [
                [
                    new three_1.Mesh(CircleGeometry(0.5 * rotationScale, 0.5), rotationMaterialRed),
                ],
            ],
            Y: [
                [
                    new three_1.Mesh(CircleGeometry(0.5 * rotationScale, 0.5), rotationMaterialGreen),
                    null,
                    [0, 0, -Math.PI / 2],
                ],
            ],
            Z: [
                [
                    new three_1.Mesh(CircleGeometry(0.5 * rotationScale, 0.5), rotationMaterialBlue),
                    null,
                    [0, Math.PI / 2, 0],
                ],
            ],
            E: [
                [
                    new three_1.Mesh(CircleGeometry(0.6 * rotationScale, 1), rotationMaterialYellowTransparent),
                    null,
                    [0, Math.PI / 2, 0],
                ],
            ],
        };
        const helperRotate = {
            AXIS: [
                [
                    new three_1.Line(rotationLineGeometry, rotationMaterialHelper.clone()),
                    [-1e3, 0, 0],
                    null,
                    [1e6, 1, 1],
                    "helper",
                ],
            ],
        };
        const pickerRotate = {
            // XYZE: [
            //     [new Mesh(new SphereGeometry(0.25 * rotationScale, 10, 8), rotationMaterialInvisible)]
            // ],
            X: [
                [
                    new three_1.Mesh(new three_1.TorusGeometry(0.5 * rotationScale, 0.1 * mobileFactorSingleAxis, 4, 24), rotationMaterialInvisible),
                    [0, 0, 0],
                    [0, -Math.PI / 2, -Math.PI / 2],
                ],
            ],
            Y: [
                [
                    new three_1.Mesh(new three_1.TorusGeometry(0.5 * rotationScale, 0.1 * mobileFactorSingleAxis, 4, 24), rotationMaterialInvisible),
                    [0, 0, 0],
                    [Math.PI / 2, 0, 0],
                ],
            ],
            Z: [
                [
                    new three_1.Mesh(new three_1.TorusGeometry(0.5 * rotationScale, 0.1 * mobileFactorSingleAxis, 4, 24), rotationMaterialInvisible),
                    [0, 0, 0],
                    [0, 0, -Math.PI / 2],
                ],
            ],
            E: [
                [
                    new three_1.Mesh(new three_1.TorusGeometry(0.6 * rotationScale, 0.1, 2, 24), rotationMaterialInvisible),
                ],
            ],
        };
        const gizmoScale = {
            X: [
                // [new Mesh(scaleHandleGeometry, scaleMaterialRed), [0.5, 0, 0], [0, 0, - Math.PI / 2]],
                // [new Mesh(scaleLineGeometry2, scaleMaterialRed), [0, 0, 0], [0, 0, - Math.PI / 2]],
                [
                    new three_1.Mesh(scaleHandleGeometry, scaleMaterialRed),
                    [-0.5, 0, 0],
                    [0, 0, Math.PI / 2],
                ],
            ],
            Y: [
                // [new Mesh(scaleHandleGeometry, scaleMaterialGreen), [0, 0.5, 0]],
                // [new Mesh(scaleLineGeometry2, scaleMaterialGreen)],
                [
                    new three_1.Mesh(scaleHandleGeometry, scaleMaterialGreen),
                    [0, -0.5, 0],
                    [0, 0, Math.PI],
                ],
            ],
            Z: [
                // [new Mesh(scaleHandleGeometry, scaleMaterialBlue), [0, 0, 0.5], [Math.PI / 2, 0, 0]],
                // [new Mesh(scaleLineGeometry2, scaleMaterialBlue), [0, 0, 0], [Math.PI / 2, 0, 0]],
                [
                    new three_1.Mesh(scaleHandleGeometry, scaleMaterialBlue),
                    [0, 0, -0.5],
                    [-Math.PI / 2, 0, 0],
                ],
            ],
            XY: [
                [
                    new three_1.Mesh(new three_1.BoxGeometry(-0.15, -0.15, 0.01), scaleMaterialBlueTransparent),
                    [
                        -0.25 * mobileFactorMultiAxis,
                        -0.25 * mobileFactorMultiAxis,
                        0,
                    ],
                ],
            ],
            YZ: [
                [
                    new three_1.Mesh(new three_1.BoxGeometry(-0.15, -0.15, 0.01), scaleMaterialRedTransparent),
                    [
                        0,
                        -0.25 * mobileFactorMultiAxis,
                        -0.25 * mobileFactorMultiAxis,
                    ],
                    [0, Math.PI / 2, 0],
                ],
            ],
            XZ: [
                [
                    new three_1.Mesh(new three_1.BoxGeometry(-0.15, -0.15, 0.01), scaleMaterialGreenTransparent),
                    [
                        -0.25 * mobileFactorMultiAxis,
                        0,
                        -0.25 * mobileFactorMultiAxis,
                    ],
                    [-Math.PI / 2, 0, 0],
                ],
            ],
            // XYZ: [
            //     [new Mesh(new BoxGeometry(0.1, 0.1, 0.1), scaleMaterialWhiteTransparent.clone())],
            // ]
        };
        const pickerScale = {
            X: [
                // [new Mesh(new CylinderGeometry(0.2, 0, 0.6, 4), scaleMaterialInvisible), [0.3, 0, 0], [0, 0, - Math.PI / 2]],
                [
                    new three_1.Mesh(new three_1.CylinderGeometry(0.15 * mobileFactorSingleAxis, 0.15 * mobileFactorSingleAxis, 0.6, 4), scaleMaterialInvisible),
                    [-0.3, 0, 0],
                    [0, 0, Math.PI / 2],
                ],
            ],
            Y: [
                // [new Mesh(new CylinderGeometry(0.2, 0, 0.6, 4), scaleMaterialInvisible), [0, 0.3, 0]],
                [
                    new three_1.Mesh(new three_1.CylinderGeometry(0.15 * mobileFactorSingleAxis, 0.15 * mobileFactorSingleAxis, 0.6, 4), scaleMaterialInvisible),
                    [0, -0.3, 0],
                    [0, 0, Math.PI],
                ],
            ],
            Z: [
                // [new Mesh(new CylinderGeometry(0.2, 0, 0.6, 4), scaleMaterialInvisible), [0, 0, 0.3], [Math.PI / 2, 0, 0]],
                [
                    new three_1.Mesh(new three_1.CylinderGeometry(0.15 * mobileFactorSingleAxis, 0.15 * mobileFactorSingleAxis, 0.6, 4), scaleMaterialInvisible),
                    [0, 0, -0.3],
                    [-Math.PI / 2, 0, 0],
                ],
            ],
            XY: [
                [
                    new three_1.Mesh(new three_1.BoxGeometry(-0.2 * mobileFactorMultiAxis, -0.2 * mobileFactorMultiAxis, 0.01), scaleMaterialInvisible),
                    [
                        -0.25 * mobileFactorMultiAxis,
                        -0.25 * mobileFactorMultiAxis,
                        0,
                    ],
                ],
            ],
            YZ: [
                [
                    new three_1.Mesh(new three_1.BoxGeometry(-0.2 * mobileFactorMultiAxis, -0.2 * mobileFactorMultiAxis, 0.01), scaleMaterialInvisible),
                    [
                        0,
                        -0.25 * mobileFactorMultiAxis,
                        -0.25 * mobileFactorMultiAxis,
                    ],
                    [0, Math.PI / 2, 0],
                ],
            ],
            XZ: [
                [
                    new three_1.Mesh(new three_1.BoxGeometry(-0.2 * mobileFactorMultiAxis, -0.2 * mobileFactorMultiAxis, 0.01), scaleMaterialInvisible),
                    [
                        -0.25 * mobileFactorMultiAxis,
                        0,
                        -0.25 * mobileFactorMultiAxis,
                    ],
                    [-Math.PI / 2, 0, 0],
                ],
            ],
            // XYZ: [
            //     [new Mesh(new BoxGeometry(0.2, 0.2, 0.2), scaleMaterialInvisible), [0, 0, 0]],
            // ]
        };
        const helperScale = {
            X: [
                [
                    new three_1.Line(scaleLineGeometry, scaleMaterialHelper.clone()),
                    [-1e3, 0, 0],
                    null,
                    [1e6, 1, 1],
                    "helper",
                ],
            ],
            Y: [
                [
                    new three_1.Line(scaleLineGeometry, scaleMaterialHelper.clone()),
                    [0, -1e3, 0],
                    [0, 0, Math.PI / 2],
                    [1e6, 1, 1],
                    "helper",
                ],
            ],
            Z: [
                [
                    new three_1.Line(scaleLineGeometry, scaleMaterialHelper.clone()),
                    [0, 0, -1e3],
                    [0, -Math.PI / 2, 0],
                    [1e6, 1, 1],
                    "helper",
                ],
            ],
        };
        // Creates an Object3D with gizmos described in custom hierarchy definition.
        function setupGizmo(gizmoMap) {
            const gizmo = new three_1.Object3D();
            for (const name in gizmoMap) {
                for (let i = gizmoMap[name].length; i--;) {
                    const object = gizmoMap[name][i][0].clone();
                    const position = gizmoMap[name][i][1];
                    const rotation = gizmoMap[name][i][2];
                    const scale = gizmoMap[name][i][3];
                    const tag = gizmoMap[name][i][4];
                    // name and tag properties are essential for picking and updating logic.
                    object.name = name;
                    object.userData.ambientOcclusion = false;
                    object.tag = tag;
                    if (position) {
                        object.position.set(position[0], position[1], position[2]);
                    }
                    if (rotation) {
                        object.rotation.set(rotation[0], rotation[1], rotation[2]);
                    }
                    if (scale) {
                        object.scale.set(scale[0], scale[1], scale[2]);
                    }
                    object.updateMatrix();
                    const tempGeometry = object.geometry.clone();
                    tempGeometry.applyMatrix4(object.matrix);
                    object.geometry = tempGeometry;
                    object.renderOrder = Infinity;
                    object.position.set(0, 0, 0);
                    object.rotation.set(0, 0, 0);
                    object.scale.set(1, 1, 1);
                    gizmo.add(object);
                }
            }
            return gizmo;
        }
        // Gizmo creation
        this.gizmo = {
            translate: setupGizmo(gizmoTranslate),
            rotate: setupGizmo(gizmoRotate),
            scale: setupGizmo(gizmoScale),
        };
        this._availablePicker = this.picker = {
            translate: setupGizmo(pickerTranslate),
            rotate: setupGizmo(pickerRotate),
            scale: setupGizmo(pickerScale),
        };
        this.helper = {
            translate: setupGizmo(helperTranslate),
            rotate: setupGizmo(helperRotate),
            scale: setupGizmo(helperScale),
        };
        this.add(this.gizmo["scale"]);
        this.add(this._availablePicker["scale"]);
        this.add(this.helper["scale"]);
        this.add(this.gizmo["translate"]);
        this.add(this._availablePicker["translate"]);
        this.add(this.helper["translate"]);
        this.add(this.gizmo["rotate"]);
        this.add(this._availablePicker["rotate"]);
        this.add(this.helper["rotate"]);
        // Pickers should be hidden always
        this._availablePicker["translate"].visible = false;
        this._availablePicker["rotate"].visible = false;
        this._availablePicker["scale"].visible = false;
    }
    get enableRotation() {
        return this._enableRotation;
    }
    set enableRotation(value) {
        this._enableRotation = value;
    }
    get enableRotationX() {
        return this._enableRotationX;
    }
    set enableRotationX(value) {
        this._enableRotationX = value;
    }
    get enableRotationXY() {
        return this._enableRotationXY;
    }
    set enableRotationXY(value) {
        this._enableRotationXY = value;
    }
    get enableRotationXZ() {
        return this._enableRotationXZ;
    }
    set enableRotationXZ(value) {
        this._enableRotationXZ = value;
    }
    get enableRotationY() {
        return this._enableRotationY;
    }
    set enableRotationY(value) {
        this._enableRotationY = value;
    }
    get enableRotationYZ() {
        return this._enableRotationYZ;
    }
    set enableRotationYZ(value) {
        this._enableRotationYZ = value;
    }
    get enableRotationZ() {
        return this._enableRotationZ;
    }
    set enableRotationZ(value) {
        this._enableRotationZ = value;
    }
    get enableScaling() {
        return this._enableScaling;
    }
    set enableScaling(value) {
        this._enableScaling = value;
    }
    get enableScalingX() {
        return this._enableScalingX;
    }
    set enableScalingX(value) {
        this._enableScalingX = value;
    }
    get enableScalingXY() {
        return this._enableScalingXY;
    }
    set enableScalingXY(value) {
        this._enableScalingXY = value;
    }
    get enableScalingXZ() {
        return this._enableScalingXZ;
    }
    set enableScalingXZ(value) {
        this._enableScalingXZ = value;
    }
    get enableScalingY() {
        return this._enableScalingY;
    }
    set enableScalingY(value) {
        this._enableScalingY = value;
    }
    get enableScalingYZ() {
        return this._enableScalingYZ;
    }
    set enableScalingYZ(value) {
        this._enableScalingYZ = value;
    }
    get enableScalingZ() {
        return this._enableScalingZ;
    }
    set enableScalingZ(value) {
        this._enableScalingZ = value;
    }
    get enableTranslation() {
        return this._enableTranslation;
    }
    set enableTranslation(value) {
        this._enableTranslation = value;
    }
    get enableTranslationX() {
        return this._enableTranslationX;
    }
    set enableTranslationX(value) {
        this._enableTranslationX = value;
    }
    get enableTranslationXY() {
        return this._enableTranslationXY;
    }
    set enableTranslationXY(value) {
        this._enableTranslationXY = value;
    }
    get enableTranslationXZ() {
        return this._enableTranslationXZ;
    }
    set enableTranslationXZ(value) {
        this._enableTranslationXZ = value;
    }
    get enableTranslationY() {
        return this._enableTranslationY;
    }
    set enableTranslationY(value) {
        this._enableTranslationY = value;
    }
    get enableTranslationYZ() {
        return this._enableTranslationYZ;
    }
    set enableTranslationYZ(value) {
        this._enableTranslationYZ = value;
    }
    get enableTranslationZ() {
        return this._enableTranslationZ;
    }
    set enableTranslationZ(value) {
        this._enableTranslationZ = value;
    }
    // updateMatrixWorld will update transformations and appearance of individual handles
    updateMatrixWorld(force) {
        var _a;
        const space = this._gumballTransformControls.space;
        let quaternion = new three_1.Quaternion();
        if (space === "local") {
            (_a = this._gumballTransformControls.object) === null || _a === void 0 ? void 0 : _a.getWorldQuaternion(quaternion);
        }
        else {
            quaternion = _identityQuaternion;
        }
        // Show only gizmos for current transform mode
        this.gizmo["translate"].visible = this.enableTranslation;
        this.gizmo["rotate"].visible = this.enableRotation;
        this.gizmo["scale"].visible =
            this.enableScaling && this._gumballTransformControls.space === "local";
        this.helper["translate"].visible = this.enableTranslation;
        this.helper["rotate"].visible = this.enableRotation;
        this.helper["scale"].visible =
            this.enableScaling && this._gumballTransformControls.space === "local";
        this.picker = this._availablePicker;
        let handles = [];
        if (this.enableTranslation) {
            let pickers = this._availablePicker.translate.children;
            let gizmos = this.gizmo.translate.children;
            let helpers = this.helper.translate.children;
            // filter out all X handles if X is disabled
            if (this.enableTranslationX === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "X");
            // filter out all Y handles if Y is disabled
            if (this.enableTranslationY === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "Y");
            // filter out all Z handles if Z is disabled
            if (this.enableTranslationZ === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "Z");
            // filter out all XY handles if XY is disabled
            if (this.enableTranslationXY === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "XY");
            // filter out all YZ handles if YZ is disabled
            if (this.enableTranslationYZ === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "YZ");
            // filter out all XZ handles if XZ is disabled
            if (this.enableTranslationXZ === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "XZ");
            // filter out all XYZ handles if XYZ is disabled
            if (this.enableTranslationX === false ||
                this.enableTranslationY === false ||
                this.enableTranslationZ === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "XYZ");
            handles = handles.concat(pickers.map((object) => ({
                object,
                mode: GumballTransformControls_1.TransformationType.TRANSLATION,
            })));
            handles = handles.concat(gizmos.map((object) => ({
                object,
                mode: GumballTransformControls_1.TransformationType.TRANSLATION,
            })));
            handles = handles.concat(helpers.map((object) => ({
                object,
                mode: GumballTransformControls_1.TransformationType.TRANSLATION,
            })));
        }
        if (this.enableRotation) {
            let pickers = this._availablePicker.rotate.children;
            let gizmos = this.gizmo.rotate.children;
            let helpers = this.helper.rotate.children;
            // filter out all X handles if X is disabled
            if (this.enableRotationX === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "X");
            // filter out all Y handles if Y is disabled
            if (this.enableRotationY === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "Y");
            // filter out all Z handles if Z is disabled
            if (this.enableRotationZ === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "Z");
            // filter out all XY handles if XY is disabled
            if (this.enableRotationXY === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "XY");
            // filter out all YZ handles if YZ is disabled
            if (this.enableRotationYZ === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "YZ");
            // filter out all XZ handles if XZ is disabled
            if (this.enableRotationXZ === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "XZ");
            // filter out all E handles if one of the axis is disabled
            if (this.enableRotationX === false ||
                this.enableRotationY === false ||
                this.enableRotationZ === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "E");
            handles = handles.concat(pickers.map((object) => ({
                object,
                mode: GumballTransformControls_1.TransformationType.ROTATION,
            })));
            handles = handles.concat(gizmos.map((object) => ({
                object,
                mode: GumballTransformControls_1.TransformationType.ROTATION,
            })));
            handles = handles.concat(helpers.map((object) => ({
                object,
                mode: GumballTransformControls_1.TransformationType.ROTATION,
            })));
        }
        if (this.enableScaling && this._gumballTransformControls.space === "local") {
            let pickers = this._availablePicker.scale.children;
            let gizmos = this.gizmo.scale.children;
            let helpers = this.helper.scale.children;
            // filter out all X handles if X is disabled
            if (this.enableScalingX === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "X");
            // filter out all Y handles if Y is disabled
            if (this.enableScalingY === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "Y");
            // filter out all Z handles if Z is disabled
            if (this.enableScalingZ === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "Z");
            // filter out all XY handles if XY is disabled
            if (this.enableScalingXY === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "XY");
            // filter out all YZ handles if YZ is disabled
            if (this.enableScalingYZ === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "YZ");
            // filter out all XZ handles if XZ is disabled
            if (this.enableScalingXZ === false)
                [pickers, gizmos, helpers] = this.filterOutAxis(pickers, gizmos, helpers, "XZ");
            handles = handles.concat(pickers.map((object) => ({
                object,
                mode: GumballTransformControls_1.TransformationType.SCALE,
            })));
            handles = handles.concat(gizmos.map((object) => ({
                object,
                mode: GumballTransformControls_1.TransformationType.SCALE,
            })));
            handles = handles.concat(helpers.map((object) => ({
                object,
                mode: GumballTransformControls_1.TransformationType.SCALE,
            })));
        }
        for (let i = 0; i < handles.length; i++) {
            const handle = handles[i];
            // hide aligned to camera
            handle.object.visible = true;
            handle.object.rotation.set(0, 0, 0);
            handle.object.position.copy(this._gumballTransformControls.worldPosition);
            let factor;
            if (this._gumballTransformControls.camera
                .isOrthographicCamera) {
                factor =
                    (this._gumballTransformControls.camera.top -
                        this._gumballTransformControls.camera
                            .bottom) /
                        this._gumballTransformControls.camera.zoom;
            }
            else {
                factor =
                    this._gumballTransformControls.worldPosition.distanceTo(this._gumballTransformControls.cameraPosition) *
                        Math.min((1.9 *
                            Math.tan((Math.PI *
                                this._gumballTransformControls
                                    .camera.fov) /
                                360)) /
                            this._gumballTransformControls.camera
                                .zoom, 7);
            }
            handle.object.scale
                .set(1, 1, 1)
                .multiplyScalar(factor * this._gumballTransformControls.size);
            // TODO: simplify helpers and consider decoupling from gizmo
            if (handle.object.tag === "helper") {
                handle.object.visible = false;
                if (handle.object.name === "AXIS") {
                    handle.object.visible = !!this._gumballTransformControls.axis;
                    if (this._gumballTransformControls.axis === "X") {
                        _tempQuaternion.setFromEuler(_tempEuler.set(0, 0, 0));
                        handle.object.quaternion
                            .copy(quaternion)
                            .multiply(_tempQuaternion);
                        if (Math.abs(_alignVector
                            .copy(_unitX)
                            .applyQuaternion(quaternion)
                            .dot(this._gumballTransformControls.eye)) > 0.9) {
                            handle.object.visible = false;
                        }
                    }
                    if (this._gumballTransformControls.axis === "Y") {
                        _tempQuaternion.setFromEuler(_tempEuler.set(0, 0, Math.PI / 2));
                        handle.object.quaternion
                            .copy(quaternion)
                            .multiply(_tempQuaternion);
                        if (Math.abs(_alignVector
                            .copy(_unitY)
                            .applyQuaternion(quaternion)
                            .dot(this._gumballTransformControls.eye)) > 0.9) {
                            handle.object.visible = false;
                        }
                    }
                    if (this._gumballTransformControls.axis === "Z") {
                        _tempQuaternion.setFromEuler(_tempEuler.set(0, Math.PI / 2, 0));
                        handle.object.quaternion
                            .copy(quaternion)
                            .multiply(_tempQuaternion);
                        if (Math.abs(_alignVector
                            .copy(_unitZ)
                            .applyQuaternion(quaternion)
                            .dot(this._gumballTransformControls.eye)) > 0.9) {
                            handle.object.visible = false;
                        }
                    }
                    if (this._gumballTransformControls.axis === "XYZE") {
                        _tempQuaternion.setFromEuler(_tempEuler.set(0, Math.PI / 2, 0));
                        _alignVector.copy(this._gumballTransformControls.rotationAxis);
                        handle.object.quaternion.setFromRotationMatrix(_lookAtMatrix.lookAt(_zeroVector, _alignVector, _unitY));
                        handle.object.quaternion.multiply(_tempQuaternion);
                        handle.object.visible = this._gumballTransformControls.dragging;
                    }
                    if (this._gumballTransformControls.axis === "E") {
                        handle.object.visible = false;
                    }
                }
                else if (handle.object.name === "START") {
                    handle.object.position.copy(this._gumballTransformControls.worldPositionStart);
                    handle.object.visible = this._gumballTransformControls.dragging;
                }
                else if (handle.object.name === "END") {
                    handle.object.position.copy(this._gumballTransformControls.worldPosition);
                    handle.object.visible = this._gumballTransformControls.dragging;
                }
                else if (handle.object.name === "DELTA") {
                    handle.object.position.copy(this._gumballTransformControls.worldPositionStart);
                    handle.object.quaternion.copy(this._gumballTransformControls.worldQuaternionStart);
                    _tempVector
                        .set(1e-10, 1e-10, 1e-10)
                        .add(this._gumballTransformControls.worldPositionStart)
                        .sub(this._gumballTransformControls.worldPosition)
                        .multiplyScalar(-1);
                    _tempVector.applyQuaternion(this._gumballTransformControls.worldQuaternionStart
                        .clone()
                        .invert());
                    handle.object.scale.copy(_tempVector);
                    handle.object.visible = this._gumballTransformControls.dragging;
                }
                else {
                    handle.object.quaternion.copy(quaternion);
                    if (this._gumballTransformControls.dragging) {
                        handle.object.position.copy(this._gumballTransformControls.worldPositionStart);
                    }
                    else {
                        handle.object.position.copy(this._gumballTransformControls.worldPosition);
                    }
                    if (this._gumballTransformControls.axis) {
                        handle.object.visible =
                            this._gumballTransformControls.axis.search(handle.object.name) !== -1;
                    }
                }
                // If updating helper, skip rest of the loop
                continue;
            }
            // Align handles to current local or world rotation
            handle.object.quaternion.copy(quaternion);
            if ((this.enableTranslation &&
                handle.mode === GumballTransformControls_1.TransformationType.TRANSLATION) ||
                (this.enableScaling && handle.mode === GumballTransformControls_1.TransformationType.SCALE)) {
                // Hide translate and scale axis facing the camera
                const AXIS_HIDE_THRESHOLD = 0.99;
                const PLANE_HIDE_THRESHOLD = 0.2;
                if (handle.object.name === "X") {
                    if (Math.abs(_alignVector
                        .copy(_unitX)
                        .applyQuaternion(quaternion)
                        .dot(this._gumballTransformControls.eye)) > AXIS_HIDE_THRESHOLD) {
                        handle.object.scale.set(1e-10, 1e-10, 1e-10);
                        handle.object.visible = false;
                    }
                }
                if (handle.object.name === "Y") {
                    if (Math.abs(_alignVector
                        .copy(_unitY)
                        .applyQuaternion(quaternion)
                        .dot(this._gumballTransformControls.eye)) > AXIS_HIDE_THRESHOLD) {
                        handle.object.scale.set(1e-10, 1e-10, 1e-10);
                        handle.object.visible = false;
                    }
                }
                if (handle.object.name === "Z") {
                    if (Math.abs(_alignVector
                        .copy(_unitZ)
                        .applyQuaternion(quaternion)
                        .dot(this._gumballTransformControls.eye)) > AXIS_HIDE_THRESHOLD) {
                        handle.object.scale.set(1e-10, 1e-10, 1e-10);
                        handle.object.visible = false;
                    }
                }
                if (handle.object.name === "XY") {
                    if (Math.abs(_alignVector
                        .copy(_unitZ)
                        .applyQuaternion(quaternion)
                        .dot(this._gumballTransformControls.eye)) < PLANE_HIDE_THRESHOLD) {
                        handle.object.scale.set(1e-10, 1e-10, 1e-10);
                        handle.object.visible = false;
                    }
                }
                if (handle.object.name === "YZ") {
                    if (Math.abs(_alignVector
                        .copy(_unitX)
                        .applyQuaternion(quaternion)
                        .dot(this._gumballTransformControls.eye)) < PLANE_HIDE_THRESHOLD) {
                        handle.object.scale.set(1e-10, 1e-10, 1e-10);
                        handle.object.visible = false;
                    }
                }
                if (handle.object.name === "XZ") {
                    if (Math.abs(_alignVector
                        .copy(_unitY)
                        .applyQuaternion(quaternion)
                        .dot(this._gumballTransformControls.eye)) < PLANE_HIDE_THRESHOLD) {
                        handle.object.scale.set(1e-10, 1e-10, 1e-10);
                        handle.object.visible = false;
                    }
                }
            }
            if (this.enableRotation) {
                // Align handle.objects to current local or world rotation
                _tempQuaternion2.copy(quaternion);
                _alignVector
                    .copy(this._gumballTransformControls.eye)
                    .applyQuaternion(_tempQuaternion.copy(quaternion).invert());
                if (handle.object.name.search("E") !== -1) {
                    handle.object.quaternion.setFromRotationMatrix(_lookAtMatrix.lookAt(this._gumballTransformControls.eye, _zeroVector, _unitY));
                }
                if (handle.object.name === "X") {
                    _tempQuaternion.setFromAxisAngle(_unitX, Math.atan2(-_alignVector.y, _alignVector.z));
                    _tempQuaternion.multiplyQuaternions(_tempQuaternion2, _tempQuaternion);
                    handle.object.quaternion.copy(_tempQuaternion);
                }
                if (handle.object.name === "Y") {
                    _tempQuaternion.setFromAxisAngle(_unitY, Math.atan2(_alignVector.x, _alignVector.z));
                    _tempQuaternion.multiplyQuaternions(_tempQuaternion2, _tempQuaternion);
                    handle.object.quaternion.copy(_tempQuaternion);
                }
                if (handle.object.name === "Z") {
                    _tempQuaternion.setFromAxisAngle(_unitZ, Math.atan2(_alignVector.y, _alignVector.x));
                    _tempQuaternion.multiplyQuaternions(_tempQuaternion2, _tempQuaternion);
                    handle.object.quaternion.copy(_tempQuaternion);
                }
            }
            // Hide disabled axes
            handle.object.visible =
                handle.object.visible &&
                    (handle.object.name.indexOf("X") === -1 ||
                        this._gumballTransformControls.showX);
            handle.object.visible =
                handle.object.visible &&
                    (handle.object.name.indexOf("Y") === -1 ||
                        this._gumballTransformControls.showY);
            handle.object.visible =
                handle.object.visible &&
                    (handle.object.name.indexOf("Z") === -1 ||
                        this._gumballTransformControls.showZ);
            handle.object.visible =
                handle.object.visible &&
                    (handle.object.name.indexOf("E") === -1 ||
                        (this._gumballTransformControls.showX &&
                            this._gumballTransformControls.showY &&
                            this._gumballTransformControls.showZ));
            // highlight selected axis
            if (handle.object.material instanceof three_1.MeshBasicMaterial ||
                handle.object.material instanceof three_1.LineBasicMaterial) {
                handle.object.material._color =
                    handle.object.material._color ||
                        handle.object.material.color.clone();
                handle.object.material._opacity =
                    handle.object.material._opacity ||
                        handle.object.material.opacity;
                handle.object.material.color.copy(handle.object.material._color);
                handle.object.material.opacity = handle.object.material._opacity;
                if (this._gumballTransformControls.enabled &&
                    this._gumballTransformControls.axis &&
                    handle.mode === this._gumballTransformControls.mode) {
                    if (handle.object.name === this._gumballTransformControls.axis) {
                        handle.object.material.color.setHex(0xffff00);
                        handle.object.material.opacity = 1.0;
                    }
                    else if (this._gumballTransformControls.axis.split("").some(function (a) {
                        return handle.object.name === a;
                    })) {
                        handle.object.material.color.setHex(0xffff00);
                        handle.object.material.opacity = 1.0;
                    }
                }
                else if (this._gumballTransformControls.enabled &&
                    this._gumballTransformControls.pivotDragged) {
                    handle.object.material.color.setHex(0xffff00);
                    handle.object.material.opacity = 1.0;
                }
            }
        }
        super.updateMatrixWorld(force);
    }
    filterOutAxis(pickers, gizmos, helpers, axis) {
        const pickersAxis = pickers.filter((object) => object.name === axis);
        const gizmosAxis = gizmos.filter((object) => object.name === axis);
        const helpersAxis = helpers.filter((object) => object.name === axis);
        [pickersAxis, gizmosAxis, helpersAxis].forEach((objects) => {
            objects.forEach((object) => {
                object.visible = false;
            });
        });
        pickersAxis.forEach((object) => {
            this.picker.translate.remove(object);
            this.picker.rotate.remove(object);
            this.picker.scale.remove(object);
        });
        pickers = pickers.filter((object) => object.name !== axis);
        gizmos = gizmos.filter((object) => object.name !== axis);
        helpers = helpers.filter((object) => object.name !== axis);
        return [pickers, gizmos, helpers];
    }
}
exports.GumballTransformGizmo = GumballTransformGizmo;
const _alignVector = new three_1.Vector3(0, 1, 0);
const _identityQuaternion = new three_1.Quaternion();
const _lookAtMatrix = new three_1.Matrix4();
const _tempEuler = new three_1.Euler();
const _tempQuaternion = new three_1.Quaternion();
const _tempQuaternion2 = new three_1.Quaternion();
const _tempVector = new three_1.Vector3();
const _unitX = new three_1.Vector3(1, 0, 0);
const _unitY = new three_1.Vector3(0, 1, 0);
const _unitZ = new three_1.Vector3(0, 0, 1);
const _zeroVector = new three_1.Vector3(0, 0, 0);
//# sourceMappingURL=GumballTransformGizmo.js.map